import { Platform, StyleSheet, Dimensions } from 'react-native';
import { FontFamily } from '../../constants/fonts';
import type { ThemeColors } from '../../theme';
import { lightThemeColors } from '../../theme/tokens/lightTheme';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
// Consider devices with width >= 768 as tablets / iPad
const IS_TABLET = SCREEN_WIDTH >= 768;

// Plain JS objects for chart label props (used by chart libraries that expect raw objects)
export const metricsChartLabelProps = {
  fontSize: 12,
  fontWeight: '400',
  fill: '#616161',
  fontFamily: FontFamily.regular,
  // keep legacy key if some libs expect it
  FontFamily: FontFamily.regular,
};

export const metricsChartVerticalLabelProps = {
  fontSize: 12,
  fontWeight: '400',
  fill: '#616161',
  fontFamily: FontFamily.regular,
  FontFamily: FontFamily.regular,
};

export const metricsChartHorizontalLabelProps = {
  fontSize: 12,
  fontWeight: '400',
  fill: '#616161',
  fontFamily: FontFamily.regular,
  FontFamily: FontFamily.regular,
};

export const createMetricsChartLabelProps = (colors: ThemeColors) => ({
  fontSize: 12,
  fontWeight: '400',
  fill: colors.text.secondary,
  fontFamily: FontFamily.regular,
  FontFamily: FontFamily.regular,
});

export const createMetricsChartVerticalLabelProps = (colors: ThemeColors) => ({
  fontSize: 12,
  fontWeight: '400',
  fill: colors.text.secondary,
  fontFamily: FontFamily.regular,
  FontFamily: FontFamily.regular,
});

export const createMetricsChartHorizontalLabelProps = (colors: ThemeColors) => ({
  fontSize: 12,
  fontWeight: '400',
  fill: colors.text.secondary,
  fontFamily: FontFamily.regular,
  FontFamily: FontFamily.regular,
});

export const createHomeScreenOverlayContainerStyle = (colors: ThemeColors) => {
  // Check if we're in light mode by comparing to known light theme colors
  // In light mode, text.primary is gray800 (#2D2D2D), in dark mode it's lightText (#F2F2F2)
  const isLightMode = colors.text.primary === '#2D2D2D'; // palette.gray800
  
  return {
    borderWidth: 10,
    borderColor: colors.border.default,
    backgroundColor: isLightMode ? '#FFFFFF' : colors.background.elevated,
  };
};

export const createChangeLogHtmlStyles = (colors: ThemeColors) => ({
  p: {
    fontSize: 14,
    lineHeight: 20,
    color: colors.text.primary,
    marginBottom: 6,
  },
  div: {
    fontSize: 14,
    lineHeight: 20,
    color: colors.text.primary,
    marginBottom: 6,
  },
  span: {
    fontSize: 14,
    lineHeight: 20,
    color: colors.text.primary,
  },
  b: {
    fontWeight: 'bold' as const,
  },
  strong: {
    fontWeight: 'bold' as const,
  },
  br: {
    height: 4,
  },
  body: {
    lineHeight: 20,
    fontSize: 14,
    color: colors.text.primary,
  },
});

export const createHomeScreenThemeStyles = (colors: ThemeColors, isDark: boolean = false) =>
  StyleSheet.create({
    screenRoot: {
      // Match Observation screen's light-mode background instead of the app-wide grey page token
      backgroundColor: isDark ? colors.background.page : "#F5F9FF",
    },
    container: {
      backgroundColor: isDark ? colors.background.page : "#F5F9FF",
    },
    cardSurface: {
      backgroundColor: colors.background.surface,
      // Light mode: 1px #D0D0D0 border + 0px 4px 16px 0px #005ec014 shadow.
      // Dark mode: 1px theme-border only (shadows don't read on dark backgrounds).
      borderWidth: 1,
      borderColor: isDark ? colors.border.default : '#D0D0D0',
      // React Native shadow (iOS + Android elevation)
      ...(isDark ? {} : {
        shadowColor: '#005EC0',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.078,
        shadowRadius: 8,
        elevation: 4,
      }),
      // Web: override with exact hex8 box-shadow in light mode
      ...(Platform.OS === 'web' && !isDark
        ? { boxShadow: '0px 4px 16px 0px #005ec014' } as any
        : {}),
    },
    elevatedSurface: {
      backgroundColor: colors.background.elevated,
      borderWidth: isDark ? 1 : 0,
      borderColor: colors.border.default,
    },
    sectionHeader: {
      color: colors.text.primary,
    },
    sectionHeaderWithMargins: {
      color: colors.text.primary,
    },
    subTextBlock: {
      color: colors.text.secondary,
    },
    primaryText: {
      color: colors.text.primary,
    },
    secondaryText: {
      color: colors.text.secondary,
    },
    inverseText: {
      color: colors.text.inverse,
    },
    brandText: {
      color: colors.brand.primary,
    },
    loadingText: {
      color: colors.text.secondary,
    },
    errorText: {
      color: colors.status.error,
    },
    iconContainer: {
      backgroundColor: colors.background.elevated,
    },
    divider: {
      backgroundColor: colors.border.divider,
      borderColor: colors.border.divider,
    },
    metricRow: {
      borderBottomColor: colors.border.divider,
    },
    metricCell: {
      color: colors.text.secondary,
    },
    metricHeaderCell: {
      color: colors.text.primary,
      backgroundColor: colors.background.elevated,
    },
    overlayNoDataTitle: {
      color: colors.text.primary,
    },
    overlayNoDataBody: {
      color: colors.text.secondary,
    },
    metricsDropdownPlaceholder: {
      color: colors.text.secondary,
    },
    metricsDropdownText: {
      color: colors.text.primary,
    },
    ciChartFooter: {
      color: colors.text.secondary,
    },
    ciActiveHeader: {
      color: colors.text.primary,
    },
    ciActiveLoadingText: {
      color: colors.text.secondary,
    },
    ciButtonHintContainer: {
      backgroundColor: colors.background.elevated,
    },
    ciButtonHintText: {
      color: colors.text.secondary,
    },
    ciSummaryText: {
      color: colors.text.secondary,
    },
    ciTable: {
      borderColor: colors.border.default,
    },
    ciTableHeader: {
      backgroundColor: colors.background.elevated,
      borderBottomColor: colors.border.default,
    },
    ciTableHeaderCell: {
      color: colors.text.primary,
    },
    ciTableRow: {
      borderBottomColor: colors.border.divider,
    },
    ciTableCell: {
      color: colors.text.secondary,
    },
    showMoreText: {
      color: colors.brand.primary,
    },
    eventText: {
      color: colors.text.secondary,
    },
    changeLogText: {
      color: colors.text.primary,
    },
    overlayDemoContainer: {
      backgroundColor: colors.background.surface,
      borderLeftColor: colors.brand.primary,
      borderWidth: 1,
      borderColor: colors.border.default,
    },
    overlayDemoTitle: {
      color: colors.text.primary,
    },
    overlayDemoDescription: {
      color: colors.text.secondary,
    },
    customContent: {
      backgroundColor: colors.background.elevated,
      borderWidth: 1,
      borderColor: colors.border.default,
    },
    customContentTitle: {
      color: colors.text.primary,
    },
    customContentText: {
      color: colors.text.secondary,
    },
    customContentExample: {
      backgroundColor: colors.background.surface,
      borderColor: colors.border.default,
    },
    customContentLabel: {
      color: colors.text.secondary,
      borderLeftColor: colors.brand.primary,
    },
    cardContentText: {
      color: colors.text.secondary,
    },
    closeOverlayButton: {
      backgroundColor: colors.background.elevated,
      borderWidth: 1,
      borderColor: colors.border.default,
    },
    closeOverlayButtonText: {
      color: colors.text.primary,
    },
    eventIconContainer: {
      backgroundColor: colors.background.elevated,
    },
    eventItemText: {
      color: colors.text.primary,
    },
    eventDateText: {
      color: colors.text.secondary,
    },
    linkCard: {
      backgroundColor: colors.background.surface,
      borderWidth: 1,
      borderColor: colors.border.default,
    },
    linkText: {
      color: colors.text.primary,
    },
    locationContainer: {
      backgroundColor: colors.background.elevated,
      borderWidth: 1,
      borderColor: colors.border.default,
    },
    locationText: {
      color: colors.text.primary,
    },
    filterLabel: {
      color: colors.text.secondary,
    },
    metricCardLabel: {
      color: colors.text.primary,
    },
    chevronDown: {
      borderTopColor: colors.text.secondary,
    },
    noDataText: {
      color: colors.text.primary,
    },
    externalSectionHeader: {
      color: colors.text.primary,
    },
    linkIconContainer: {
      backgroundColor: colors.background.elevated,
    },
    linkTextPrimary: {
      color: colors.text.primary,
    },
    linkTextSecondary: {
      color: colors.text.secondary,
      fontSize: 14,
      lineHeight: 20,
      fontFamily: FontFamily.regular,
      marginTop: 2,
    },
    linkDivider: {
      backgroundColor: colors.border.divider,
    },
    externalLoadingText: {
      color: colors.text.secondary,
    },
    safetyLoadingText: {
      color: colors.text.secondary,
    },
    changeLogMetaText: {
      fontSize: 12,
      color: colors.text.primary,
      textAlign: 'right',
    },
    changeLogMetaLabel: {
      fontWeight: '700',
      color: colors.text.primary,
    },
    viewDetailsText: {
      fontSize: 12,
      fontFamily: FontFamily.bold,
      color: colors.brand.primary,
      fontWeight: '700',
    },
    metricCardTitle: {
      fontSize: 12,
      color: colors.text.primary,
      marginBottom: 4,
      fontWeight: '500',
    },
  });

const lightStyles = StyleSheet.create({
  screenRoot: {
    flex: 1,
    backgroundColor: "#F5F9FF",
  },
  container: {
    flex: 1,
    backgroundColor: "#F5F9FF",
    paddingHorizontal: Platform.OS === "web" ? "1%" : 0,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    padding: "1.5%",
    backgroundColor: "#0066CC",
    elevation: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    marginLeft: 1,
  },
  logo: {
    height: 36,
    width: 40,
    marginRight: 10,
  },
  headerSpacer: {
    flex: 1,
  },
  content: {
    flex: 1,
    padding: 16,
  },
  welcomeCard: {
    backgroundColor: "white",
    borderRadius: 8,
    marginBottom: 16,
    borderLeftWidth: 4,
    borderLeftColor: "#0066CC",
  },
  welcomeContainer: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
  },
  welcomeText: {
    fontSize: 24,
    fontWeight: "500",
    color: "#212529",
    marginLeft: 12,
  },
  filterContainer: {
    backgroundColor: "#F8F9FA",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  filterDropdown: {
    width: Platform.OS === "web" ? "24%" : "100%",
    marginBottom: 12,
  },
  gridContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  topSection: {
    width: "100%",
    marginBottom: Platform.OS === "web" ? 16 : 8, // Use 8px on mobile to match the gap in topRowMobile
  },
  topRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    // height: Platform.OS === 'web'?'90%': 'auto', // Adjust height for web to ensure proper layout
    gap: Platform.OS === "web" ? 16 : 0, // Add consistent spacing between items on web
  },
  topRowMobile: {
    flexDirection: "column",
    gap: 8, // Use consistent 8px gap for all mobile platforms
  },
  topLeftCard: {
    backgroundColor: "white",
    padding: 16,
    marginBottom: Platform.OS === "web" ? 0 : 10,
    width: Platform.OS === "web" ? "66%" : "100%",
    borderRadius: 8,
    minHeight: Platform.OS === "web" ? "100%" : "auto",
  },
  topRightCard: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 16,
    marginBottom: Platform.OS === "web" ? 0 : 10,
    width: Platform.OS === "web" ? "32%" : "100%",
    display: "flex",
    justifyContent: "center",
    minHeight: Platform.OS === "web" ? "100%" : "auto",
  },
  bottomSection: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginTop: 0, // Remove margin completely to be consistent with spacing between other components
  },
  column: {
    width: Platform.OS === "web" ? "48%" : "100%",
    minWidth: 300,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 16,
  },
  card: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  bottomCard: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 24,
    marginBottom: 16,
    width: Platform.OS === "web" ? "32%" : "100%",
  },
  fullWidthCard: {
    width: "100%",
    backgroundColor: "white",
    borderRadius: 8,
    padding: 24,
    marginBottom: 24,
    marginTop: Platform.OS === "web" ? 0 : 10,
  },
  sectionHeader: {
    fontSize: 16, // Figma: Body/$font-body-bold 16px
    fontWeight: "700", // Figma: Bold
    color: "#4B4B4B", // Figma: #4B4B4B
    marginBottom: 14,
    fontFamily: FontFamily.medium,
  },
  // Section header with Figma margins
  sectionHeaderWithMargins: {
    fontSize: 16,
    fontWeight: "700",
    color: "#616161",
    marginTop: 24,
    marginLeft: 24,
  },
  // Reusable subtext block used for loading and subtitle text under headers
  subTextBlock: {
    marginTop: 24,
    marginLeft: 24,
    marginBottom: 32,
    color: "#6C757D",
    fontSize: 14,
    fontFamily: FontFamily.regular,
  },
  metricsScrollView: {
    flex: 1,
    width: "100%",
  },
  metricsScrollViewContent: {
    flexGrow: 1,
  },
  metricsContainer: {
    width: "100%", // Use full available width
    minWidth: "100%", // Set a minimum width that fits all columns
  },
  metricRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E6E6E6",
    width: "100%", // Ensure rows take full width
  },
  metricCell: {
    padding: 12,
    textAlign: "center",
    flex: 1, // Use flex for responsive width distribution
    minWidth: Platform.OS === "web" ? 80 : 60, // Minimum width to ensure readability
    color: "#000000",
    fontFamily: FontFamily.regular,
  },
  metricHeaderCell: {
    fontWeight: "bold",
    color: "#000000",
    backgroundColor: "#FFFFFF",
    minHeight: 60,
    paddingVertical: 16,
    fontFamily: FontFamily.medium,
  },
  metricBoldText: {
    fontWeight: '700',
    fontFamily: FontFamily.medium,
  },
  metricDefaultText: {
    fontFamily: FontFamily.regular,
  },
  metricHeaderText: {
    fontFamily: FontFamily.medium,
  },
  // Overlay no-data text styles used by MetricsOverlay
  overlayNoDataTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#6C757D',
    fontFamily: FontFamily.medium,
  },
  overlayNoDataBody: {
    fontSize: 14,
    color: '#6C757D',
    marginTop: 8,
    textAlign: 'center',
    fontFamily: FontFamily.regular,
  },
  // Dropdown and small helper text used in MetricsSection - Figma spec
  metricsDropdownPlaceholder: {
    color: '#3A3A3A', // Figma: Input text color
    fontSize: 12, // Figma: XSmall/$font-xsmall-regular
    fontFamily: FontFamily.regular,
  },
  metricsDropdownText: {
    fontSize: 12, // Figma: XSmall/$font-xsmall-regular
    color: '#3A3A3A', // Figma: #3A3A3A
    fontFamily: FontFamily.regular,
  },
  metricsViewDetailsText: {
    fontSize: 12, // Figma: XSmall/$font-xsmall-bold
    color: '#005CBC', // Figma: #005CBC
    textAlign: 'right',
    fontFamily: FontFamily.bold,
  },
  ciChartFooter: {
    textAlign: "center",
    marginTop: 16,
    fontStyle: "italic",
    color: "#666",
  },
  ciButtonsContainer: {
    flexDirection: "column",
    justifyContent: "space-around",
    // marginVertical: 16,
  },
  /* Styles specific to the CI Active Items component (moved from local component file)
     These mirror the Figma tokens: 24px offsets, 12px gaps, 348px max width and 48px height */
  ciActiveContainer: {
    paddingTop: 24,
    paddingLeft: 24,
    paddingRight: 24,
    paddingBottom: 24,
  },
  ciActiveHeader: {
    fontSize: 16,
    fontWeight: "700",
    color: "#616161",
    marginBottom: 20,

    // header is plain text in the card (no background)
    fontFamily: FontFamily.medium,
  },
  ciActiveContentContainer: {
    flex: 1,
    justifyContent: "center",
  },
  ciActiveButtonsContainer: {
    width: "100%",
    maxWidth: 348,
    alignSelf: "center",
    marginLeft: 24,
    marginRight: 24,
    ...(IS_TABLET
      ? {
          // On tablet/iPad make the buttons container stretch to full width like web
          maxWidth: undefined,
          marginLeft: 24,
          marginRight: 24,
        }
      : {}),
  },
  ciActiveButton: {
    height: 48,
    width: "100%",
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 0, // explicitly remove borders
    // background color applied inline per button to use Figma tokens
  },
  ciActiveButtonRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  ciActiveButtonLabel: {

    fontWeight: "400",
    fontSize: 16,
    color: "#EFEFEF",
    fontFamily: FontFamily.regular,
  },
  ciActiveButtonValue: {

    fontWeight: "700",
    fontSize: 16,
    color: "#EFEFEF",
    fontFamily: FontFamily.medium,
  },
  ciActiveButtonText: {
    fontSize: 14,

    textAlign: "center",
  },
  ciActiveLoadingContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 20,
    marginBottom: 20,
    height: 140,
    width: "100%",
  },
  ciActiveLoadingText: {
    marginLeft: 8,
    fontSize: 14,
    color: "#666",
    fontFamily: FontFamily.regular,
  },
  metricsButtonContainer: {
    flexDirection: "row",
    justifyContent: "center",
    marginVertical: 16,
  },
  metricsButton: {
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
    width: "45%",
    height: 70,
    alignItems: "center",
    backgroundColor: "#0066CC",
  },
  metricsButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "bold",
  },
  ciButton: {
    // backgroundColor: "green",
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
    width: "100%",
    // Slightly increased height to accommodate the new layout on small screens;
    // use the same compact height as web/tablet (48) on tablet devices
    height: IS_TABLET ? 48 : 65,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
  },
  ciButtonActive: {
    backgroundColor: "#004B93",
  },
  ciButtonWrapper: {
    width: "45%",
    alignItems: "center",
  },
  ciButtonHintContainer: {
    marginTop: 4,
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  ciButtonHintText: {
    fontSize: 12,
    color: "#495057",
    textAlign: "center",
  },
  ciSummaryText: {
    textAlign: "center",
    marginTop: 10,
    fontSize: 14,
    color: "#6C757D",
  },
  ciTable: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#DEE2E6",
    marginTop: 16,
  },
  ciTableHeader: {
    flexDirection: "row",
    backgroundColor: "#E9ECEF",
    borderBottomWidth: 1,
    borderBottomColor: "#DEE2E6",
  },
  ciTableHeaderCell: {
    padding: 10,
    fontWeight: "bold",
    fontSize: 14,
    color: "#212529",
    flex: 1,
  },
  ciTableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#DEE2E6",
  },
  ciTableCell: {
    padding: 10,
    fontSize: 13,
    color: "#495057",
    flex: 1,
  },
  ciTableWrapper: {
    flex: 1,
    width: "100%",
  },
  safetyImageContainer: {
    overflow: "hidden",
    borderRadius: 4,
    width: "100%",
    marginBottom: 8,
  },
  safetyImageTouchable: {
    width: "100%",
  },
  safetyImage: {
    width: "100%",
    height: 200,
    borderRadius: 4,
  },
  safetyTopics: {
    marginVertical: 8,
  },
  safetyTopic: {
    backgroundColor: "#004B93",
    borderRadius: 4,
    padding: 8,
    marginVertical: 4,
  },
  safetyTopicText: {
    color: "white",
    fontWeight: "500",
  },
  showMoreButton: {
    alignSelf: "center",
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginTop: 4,
  },
  showMoreText: {
    color: "#005CBC", // var(--Tokens-Link-Default, #005CBC)
    fontWeight: "700", // XSmall/$font-xsmall-bold
    fontSize: 12, // var(--Font-Size-XSmall, 12px)
    lineHeight: 18, // var(--Line-Height-XSmall, 18px)
  },
  scrollContent: {
    maxHeight: 320, // Adjusted to fit within the bottomCard height
  },
  eventText: {
    marginBottom: 8,
    color: "#495057",
  },
  changeLogText: {
    color: "#495057",
    marginBottom: 8,
  },
  overlayDemoContainer: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 20,
    marginVertical: 16,
    borderLeftWidth: 4,
    borderLeftColor: "#0066CC",
  },
  overlayDemoTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#212529",
    marginBottom: 8,
  },
  overlayDemoDescription: {
    fontSize: 14,
    color: "#6C757D",
    marginBottom: 16,
  },
  demoButtonsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 16,
  },
  demoButton: {
    backgroundColor: "#0066CC",
    borderRadius: 4,
    padding: 12,
    marginRight: 12,
    marginBottom: 12,
    minWidth: 150,
  },
  demoButtonText: {
    color: "white",
    fontWeight: "500",
    textAlign: "center",
  },
  customContent: {
    padding: 16,
    backgroundColor: "#F8F9FA",
    borderRadius: 8,
  },
  customContentTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#212529",
    marginBottom: 16,
  },
  customContentText: {
    fontSize: 16,
    color: "#495057",
    marginBottom: 16,
  },
  customContentExample: {
    backgroundColor: "white",
    padding: 16,
    borderRadius: 4,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#DEE2E6",
  },
  customContentLabel: {
    fontSize: 14,
    color: "#495057",
    marginBottom: 8,
    paddingLeft: 8,
    borderLeftWidth: 3,
    borderLeftColor: "#0066CC",
  },
  customContentButton: {
    backgroundColor: "#0066CC",
    borderRadius: 4,
    padding: 12,
    alignItems: "center",
    marginTop: 8,
  },
  customContentButtonText: {
    color: "white",
    fontWeight: "500",
  },
  customCard: {
    marginBottom: 20,
    marginTop: 10,
    width: "100%",
  },
  cardContent: {
    padding: 10,
  },
  cardContentText: {
    fontSize: 14,
    color: "#333",
    marginBottom: 8,
    lineHeight: 20,
  },
  viewMoreButton: {
    backgroundColor: "#0066CC",
    borderRadius: 4,
    padding: 10,
    alignItems: "center",
    marginTop: 8,
  },
  viewMoreButtonText: {
    color: "white",
    fontWeight: "500",
  },
  overlayContent: {
    padding: 16,
  },
  closeOverlayButton: {
    backgroundColor: "#E9ECEF",
    borderRadius: 4,
    padding: 10,
    alignItems: "center",
    marginTop: 16,
  },
  closeOverlayButtonText: {
    color: "#333",
    fontWeight: "500",
  },
  // (removed transparent close button styles - not used)
  eventItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12
    // borderBottomWidth: 1,
    // borderBottomColor: "#D7E5F8",
  },
  eventIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#EFF5FC",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  eventIcon: {
    color: "white",
    fontSize: 18,
  },
  eventTextContainer: {
    flex: 1,
  },
  eventItemText: {
    fontSize: 14,
    color: "#343A40",
    marginBottom: 4,
    fontFamily: FontFamily.medium,
  },
  eventDateText: {
    fontSize: 14,
    color: "#6C757D",
    fontFamily: FontFamily.regular,
  },
  linkCard: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  linkText: {
    fontSize: 16,
    color: "#343A40",
    marginBottom: 8,
    fontFamily: FontFamily.regular,
  },
  linkButton: {
    backgroundColor: "#0066CC",
    borderRadius: 4,
    padding: 10,
    alignItems: "center",
  },
  linkButtonText: {
    color: "white",
    fontWeight: "500",
    fontFamily: FontFamily.medium,
  },
  locationContainer: {
    padding: 16,
    backgroundColor: "#F8F9FA",
    borderRadius: 8,
    marginBottom: 16,
  },
  locationText: {
    fontSize: 16,
    color: "#343A40",
  },
  errorText: {
    fontSize: 16,
    color: "red",
  },
  ciButtonText: {
    fontWeight: "bold",
    fontSize: 16,
    color: "#212529",
  },
  ciButtonTextWithHint: {
    fontSize: Platform.OS === "web" ? 16 : 14,
    textAlign: "center",
    lineHeight: 22,
  },
  // Metrics filters section
  metricsFiltersContainer: {
    width: "100%",
    zIndex: -99999, // Ensure dropdowns appear above other content
  },
  metricsDropdownsRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    marginRight: 4,
    flexWrap: Platform.OS === "web" ? "nowrap" : "wrap",
  },
  dropdownContainer: {
    width: Platform.OS === "web" ? "30%" : "45%",
  },
  periodDropdown: {
    marginRight: 8,
  },
  weekDropdown: {
    marginRight: 8,
  },
  proceedButtonContainer: {
    alignSelf: "center",
    marginBottom: 10,
  },
  proceedButton: {
    minWidth: 100,
  },
  // New styles for metrics section based on the image
  timeframeFilter: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    marginBottom: 8,
  },
  filterLabel: {
    fontSize: 14,
    color: "#555",
    marginRight: 8,
  },
  metricsCardsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    // marginTop: 12,
  },
  metricCardItem: {
    flex: 1,
    alignItems: "center",
    marginHorizontal: 4,
  },
  metricCardLabel: {
    fontSize: 12,
    color: "#333",
    marginBottom: 4,
    fontWeight: "500",
  },
  metricCardValue: {
    height: 40,
    width: "100%",
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
  },
  metricCardValueText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  expandButton: {
    alignSelf: "center",
    marginTop: 8,
    padding: 8,
  },
  chevronDown: {
    width: 0,
    height: 0,
    backgroundColor: "transparent",
    borderStyle: "solid",
    borderLeftWidth: 8,
    borderRightWidth: 8,
    borderTopWidth: 12,
    borderLeftColor: "transparent",
    borderRightColor: "transparent",
    borderTopColor: "#666",
  },
  // No data styles
  noDataContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 60,
  },
  noDataText: {
    fontSize: 16,
    color: "#666",
    textAlign: "center",
    fontStyle: "italic",
    fontFamily: FontFamily.regular,
  },
  /* External Links (Figma) */
  externalSectionHeader: {
    fontSize: 16,
    fontWeight: '700',
    color: '#616161',
    marginBottom: 12,
    fontFamily: FontFamily.medium,
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 0,
  },
  linkIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#EFF5FC',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  linkTextContainer: {
    flex: 1,
  },
  linkTextPrimary: {
    // Figma tokens: link default color and small/regular typography
    color: '#005CBC',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 20,
    fontFamily: FontFamily.regular,
  },
  linkDivider: {
    height: 1,
    backgroundColor: '#D7E5F8',
    marginTop: 8,
    marginBottom: 8,
    //marginLeft: 24, // align with text start (icon 40 + marginRight 12)
  },
  externalLoadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  externalLoadingText: {
    marginTop: 12,
    color: '#666',
    fontSize: 14,
    textAlign: 'center',
    fontFamily: FontFamily.regular,
  },
  linkTextSecondary: {
    fontSize: 14,
    color: '#6C757D',
    lineHeight: 20,
    fontFamily: FontFamily.regular,
    marginTop: 2,
  },
  safetyLoadingText: {
    marginTop: 8,
    color: '#666',
    fontSize: 12,
    textAlign: 'center',
    fontFamily: FontFamily.regular,
  },
});

export default lightStyles;
